/** Web App Studio (WP-M29-WEBAPP-STUDIO): the studio in the console (`console`) and the strings the hosted chat adds (`app`). */
export const webappStudio = {
  app: {
    share: {
      title: "Share {{name}}",
      hint: "Anyone with this link can open the assistant and start a chat.",
      signedInOnly: "This assistant is for signed-in customers only, so there is no public link to share. Customers reach it from the website or app they sign in to.",
      signedInOnlyHint: "Nothing from your session is ever put in a link.",
      linkLabel: "Link to this assistant",
      copy: "Copy link",
      copied: "Link copied",
      copyFailed: "Could not copy. Select the link and copy it by hand.",
      native: "Share…",
      qrLabel: "QR code for the link to this assistant",
      qrHint: "Scan with a phone camera to open the chat.",
      private: "The link never includes your sign-in, a launch code or any conversation.",
      dialogTitle: "Share",
      dialogHint: "Choose what to share.",
      choiceLabel: "What would you like to share?",
      modes: { conversation: "This conversation", assistant: "The assistant" },
      conversation: {
        title: "Share this conversation",
        explain: "Anyone with the link can read the messages in this chat so far, without signing in. Messages you send later are not included. Codes, PINs and card numbers stay hidden, and your name and account details are never shown.",
        create: "Create link",
        creating: "Creating link…",
        created: "Link created. Copy it and send it to anyone.",
        linkLabel: "Link to this conversation",
        qrLabel: "QR code for the link to this conversation",
        qrHint: "Scan with a phone camera to read the conversation.",
        copied: "Conversation link copied",
        another: "Create another link",
        failed: "We couldn't create the link. Please try again.",
        disabled: "Sharing is turned off for this assistant, so this conversation can't be shared.",
        tooMany: "You have made a lot of links just now. Please wait a little, then try again.",
        limit: "This chat already has the most shared links. Stop sharing one first.",
        nothing: "Send a message and wait for the answer first, then you can share the chat.",
        unsafe: "We couldn't make a safe link. Please try again.",
      },
      links: {
        title: "Shared links",
        count_one: "{{count}} link is open",
        count_other: "{{count}} links are open",
        emptyTitle: "No shared links yet",
        emptyBody: "When you create a link to this conversation, it is listed here so you can stop sharing it.",
        row: "Shared {{when}}",
        messages_one: "{{count}} message",
        messages_other: "{{count}} messages",
        expires: "Expires {{when}}",
        stop: "Stop sharing",
        stopLabel: "Stop sharing the link from {{when}}",
        stopped: "Stopped. That link no longer opens.",
        stopFailed: "We couldn't stop sharing. Please try again.",
        loadFailed: "We couldn't load your shared links.",
      },
    },
  snapshot: {
    title: "Shared conversation with {{name}}",
    banner: "Shared conversation · read-only",
    bannerHint: "This is a copy of a chat. Nothing here can be changed or answered.",
    asOf: "Shared {{when}}",
    log: "Messages in the shared conversation",
    start: "Start your own chat",
    startHint: "Ask {{name}} your own question.",
    notFoundTitle: "This shared conversation isn't available",
    notFoundBody: "The person who shared it may have stopped sharing, or the link has expired. Ask them for a new link.",
    goHome: "Open {{name}}",
    errorTitle: "We couldn't load this conversation",
    errorBody: "Check your connection and try again.",
    busyTitle: "Too many people are opening links right now",
    busyBody: "Please wait a moment and try again.",
    loading: "Loading the shared conversation",
    team: "Team",
  },
  },
  console: {
    title: "Web App Studio",
    subtitle: "Design the hosted chat page of {{agent}}. Changes go live as soon as you save.",
    tabsLabel: "Web app settings",
    tabs: { branding: "Branding", icons: "Icons", content: "Content", layout: "Layout", share: "Share" },
    scope: {
      label: "Editing",
      assistant: "This assistant",
      default: "Workspace default",
      assistantHint: "Only {{agent}} uses this design.",
      defaultHint: "Used by every assistant that has no design of its own. Pictures are set per assistant.",
      locked: "Save or discard your changes to switch.",
    },
    source: {
      assistant: "{{agent}} uses its own design.",
      default: "{{agent}} follows the workspace default design.",
      widget: "No design saved yet: the web app follows Widget Studio (avatar and colour) and the workspace logo.",
    },
    branding: {
      appName: "App name",
      appNamePlaceholder: "Defaults to your workspace app name",
      appNameHint: "Shown in the sidebar and the browser tab.",
      logo: "Logo",
      logoHint: "Empty uses the workspace logo from Settings > Organization.",
      avatar: "Assistant avatar",
      avatarWidget: "Same as Widget",
      avatarCustom: "Upload here",
      avatarInitials: "Initials",
      avatarHint: "\"Same as Widget\" shows the avatar set in Widget Studio, then the workspace logo.",
      favicon: "Favicon",
      faviconHint: "The small icon in the browser tab. PNG, JPEG or WebP up to 2 MB.",
      upload: "Upload",
      uploading: "Uploading…",
      replace: "Replace",
      remove: "Remove",
      uploaded: "{{name}} uploaded",
      invalid: "Use a PNG, JPEG or WebP image up to 2 MB.",
      failed: "The upload failed. Try again.",
      accent: "Accent colour",
      accentSame: "Same as Widget",
      accentCustom: "Custom",
      accentHint: "\"Same as Widget\" uses the colour from Widget Studio.",
      theme: "Default theme",
      themeHint: "Visitors can still switch theme themselves; their choice wins.",
      themeSystem: "Match the device",
      themeLight: "Light",
      themeDark: "Dark",
      density: "Density",
      comfortable: "Comfortable",
      compact: "Compact",
      radius: "Corner radius",
      radiusWidget: "Same as Widget",
      radiusCustom: "Custom",
      radiusValue: "{{px}} px",
      noPictures: "Pictures belong to one assistant. Switch to \"This assistant\" to upload them.",
    },
    icons: {
      hint: "Icons keep their accessible names; only the picture changes.",
      assistantIcon: "Assistant icon",
      assistantAvatar: "Avatar picture",
      cardIcon: "Suggestion card icon",
      cardAuto: "Pick from the question",
      cardNone: "No icon",
      sendStyle: "Send button",
      sendIcon: "Icon only",
      sendLabel: "Icon and \"Send\"",
      icons: {
        bot: "Bot",
        sparkles: "Sparkles",
        "graduation-cap": "Graduation cap",
        headset: "Headset",
        "message-circle": "Chat bubble",
        "life-buoy": "Life buoy",
        "book-open": "Open book",
        brain: "Brain",
        heart: "Heart",
        star: "Star",
        zap: "Bolt",
        "building-2": "Building",
        "help-circle": "Question mark",
        "file-text": "Document",
        wallet: "Wallet",
        calendar: "Calendar",
        "map-pin": "Map pin",
        "list-checks": "Checklist",
      },
    },
    content: {
      hint: "Leave a field empty to keep the standard wording. Filipino falls back to English.",
      title: "Welcome title",
      titlePlaceholder: "Hi there! How can I help today?",
      subtitle: "Welcome subtitle",
      placeholder: "Message box placeholder",
      disclaimer: "Footer disclaimer",
      starters: "Suggested questions",
      startersWidget: "Same as the assistant's starter questions",
      startersCustom: "Write my own",
      startersEmpty: "No starter questions yet. Add some in Widget Studio, or write your own here.",
      startersMax: "Up to {{max}} questions.",
      addStarter: "Add a question",
      starterLabel: "Question {{n}}",
      removeStarter: "Remove question {{n}}",
      moveUp: "Move question {{n}} up",
      moveDown: "Move question {{n}} down",
      en: "English",
      fil: "Filipino",
    },
    layout: {
      showHistory: "Show conversation history",
      showHistoryHint: "The sidebar list and search of earlier chats. New chat always stays.",
      guestNotice: "Guest notice",
      guestNoticeHint: "Told to visitors who chat without signing in.",
      poweredBy: "\"Powered by Sagot\"",
      poweredByHint: "Same rule as Widget Studio: it can only be hidden on plans that remove branding.",
      poweredByWidget: "Follow Widget Studio",
      poweredByShow: "Always show",
      poweredByHide: "Hide",
      poweredByLocked: "Hiding it is part of plans that remove branding.",
    },
    share: {
      enabled: "Show the Share button",
      enabledHint: "Visitors can copy the public link, use their device's share sheet or scan a QR code.",
      link: "Public link",
      linkHint: "A verified custom domain when you have one, else the platform address. It never contains a sign-in or a launch code.",
      copy: "Copy link",
      copied: "Link copied",
    },
    preview: {
      live: "Live preview",
      liveHint: "of the web app, with your unsaved changes",
      device: "Preview size",
      desktop: "Desktop",
      mobile: "Mobile",
      theme: "Preview theme",
      light: "Light",
      dark: "Dark",
      open: "Open the web app",
      openLabel: "Open the web app in a new tab",
      unsaved: "Unsaved changes",
      assistantName: "Assistant",
    },
    save: "Save",
    saving: "Saving…",
    discard: "Discard",
    saved: "Web app design saved",
    saveFailed: "The design could not be saved.",
    reset: "Use the workspace default",
    resetDefault: "Clear the workspace default",
    resetDone: "Design reset",
    resetFailed: "The design could not be reset.",
    loadError: "Web App Studio could not load.",
    loadErrorBody: "Check your connection and try again.",
    notFoundTitle: "Assistant not found",
    notFoundBody: "This assistant may have been deleted. Pick another from the agent menu.",
    readOnly: "You can view this design but not change it.",
  },
} as const;
